import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Typography,
} from '@mui/material';
import { format, parseISO, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import { revalidateCustomerRow } from '../../utils/freshaCustomerCsv';
import { BRAND } from '../../theme/brandTokens';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

const emptyDraft = {
  name: '',
  email: '',
  phoneCountryCode: '+351',
  phoneNumber: '',
  genre: '',
  birthDate: null,
};

const inputStyle = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
});

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const parsed = parseISO(String(value));
  return isValid(parsed) ? parsed : null;
};

const toIsoDateOrNull = (value) => {
  if (!value || !(value instanceof Date) || !isValid(value)) return null;
  return format(value, 'yyyy-MM-dd');
};

const getDraftFieldErrors = (draft) => {
  const errors = [];
  const name = (draft.name || '').trim();
  const email = (draft.email || '').trim();
  const hasPhone = Boolean((draft.phoneCountryCode || '').trim() && (draft.phoneNumber || '').trim());

  if (!name) {
    errors.push({ field: 'name', key: 'dataImport.customers.errors.nameRequired' });
  }
  if (email) {
    if (!EMAIL_REGEX.test(email)) {
      errors.push({ field: 'email', key: 'dataImport.customers.errors.emailInvalid' });
    }
  } else if (!hasPhone) {
    errors.push({ field: 'contact', key: 'dataImport.customers.errors.contactRequired' });
  }

  return errors;
};

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: '0.95rem',
  color: BRAND.primaryText,
  letterSpacing: 0.2,
};

const FixFailedImportRowsDialog = ({
  open,
  onClose,
  rows,
  onSaveRow,
}) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setDraft(emptyDraft);
      return;
    }

    if (!rows?.length) {
      setSelectedId(null);
      return;
    }

    const nextSelected = rows.find((row) => row.id === selectedId) || rows[0];
    setSelectedId(nextSelected.id);
    setDraft({
      name: nextSelected.name || '',
      email: nextSelected.email || '',
      phoneCountryCode: nextSelected.phone?.countryCode || '+351',
      phoneNumber: nextSelected.phone?.number || '',
      genre: nextSelected.genre == null ? '' : String(nextSelected.genre),
      birthDate: toDateOrNull(nextSelected.birthDate),
    });
  }, [open, rows, selectedId]);

  const selectedRow = rows?.find((row) => row.id === selectedId);
  const draftErrors = useMemo(() => getDraftFieldErrors(draft), [draft]);
  const hasNameError = draftErrors.some((error) => error.field === 'name');
  const hasEmailError = draftErrors.some((error) => error.field === 'email');

  const handleSave = () => {
    if (!selectedRow) return;

    const phone = draft.phoneNumber?.trim()
      ? {
        countryCode: draft.phoneCountryCode.trim() || '+351',
        number: draft.phoneNumber.trim(),
      }
      : null;

    const updated = revalidateCustomerRow({
      ...selectedRow,
      name: draft.name,
      email: draft.email,
      phone,
      genre: draft.genre === '' ? null : Number(draft.genre),
      birthDate: toIsoDateOrNull(draft.birthDate),
    });

    onSaveRow(updated);
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('dataImport.customers.fixDialog.title')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSave} disabled={!selectedRow || draftErrors.length > 0}>
            {t('dataImport.customers.fixDialog.apply')}
          </PrimaryActionButton>
        </>
      )}
    >
      {!rows?.length ? (
        <Alert severity="success">{t('dataImport.customers.fixDialog.noFailures')}</Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SalovareSelectField
            label={t('dataImport.customers.fixDialog.selectRow')}
            value={selectedId || ''}
            onChange={(event) => setSelectedId(event.target.value)}
            displayEmpty
          >
            {rows.map((row) => (
              <MenuItem key={row.id} value={row.id}>
                {t('dataImport.customers.fixDialog.rowLabel', {
                  row: row.rowNumber,
                  name: row.name || row.original?.name || '—',
                })}
              </MenuItem>
            ))}
          </SalovareSelectField>

          {selectedRow && (
            <>
              <Box>
                <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                  {t('dataImport.customers.fixDialog.rawRow')}
                </Typography>
                <Box
                  sx={{
                    maxHeight: 180,
                    overflow: 'auto',
                    border: `1px solid ${BRAND.line}`,
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: BRAND.background,
                  }}
                >
                  {selectedRow.rawCells?.map((cell) => (
                    <Typography key={`${selectedRow.id}-${cell.header}`} variant="body2" sx={{ mb: 0.5 }}>
                      <strong>{cell.header}:</strong> {cell.value || '—'}
                    </Typography>
                  ))}
                </Box>
              </Box>

              {draftErrors.length > 0 && (
                <Alert severity="error">
                  {draftErrors.map((error) => t(error.key)).join(' · ')}
                </Alert>
              )}

              <Divider />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={sectionTitleSx}>
                    {t('dataImport.customers.fixDialog.requiredSection')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('dataImport.customers.fixDialog.requiredSectionHelp')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={fieldLabelSx}>
                    {t('dataImport.customers.fields.name')} *
                  </Typography>
                  {selectedRow.original?.name ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('dataImport.customers.fixDialog.originalValue', { value: selectedRow.original.name })}
                    </Typography>
                  ) : null}
                  <Box sx={inputWrapperSx}>
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                      style={inputStyle(hasNameError)}
                      onFocus={(event) => { event.target.style.borderColor = BRAND.accent; }}
                      onBlur={(event) => {
                        event.target.style.borderColor = hasNameError ? '#f44336' : BRAND.line;
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={fieldLabelSx}>
                    {t('dataImport.customers.fields.email')} *
                  </Typography>
                  {selectedRow.original?.email !== undefined ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('dataImport.customers.fixDialog.originalValue', {
                        value: selectedRow.original.email || '—',
                      })}
                    </Typography>
                  ) : null}
                  <Box sx={inputWrapperSx}>
                    <input
                      type="email"
                      value={draft.email}
                      onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
                      style={inputStyle(hasEmailError)}
                      onFocus={(event) => { event.target.style.borderColor = BRAND.accent; }}
                      onBlur={(event) => {
                        event.target.style.borderColor = hasEmailError ? '#f44336' : BRAND.line;
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={sectionTitleSx}>
                    {t('dataImport.customers.fixDialog.optionalSection')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('dataImport.customers.fixDialog.optionalSectionHelp')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={fieldLabelSx}>
                    {t('dataImport.customers.fields.phone')}
                  </Typography>
                  {selectedRow.original?.phone ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('dataImport.customers.fixDialog.originalValue', { value: selectedRow.original.phone })}
                    </Typography>
                  ) : null}
                  <SalovarePhoneInput
                    countryCode={draft.phoneCountryCode}
                    number={draft.phoneNumber}
                    onCountryCodeChange={(value) => setDraft((prev) => ({ ...prev, phoneCountryCode: value }))}
                    onNumberChange={(value) => setDraft((prev) => ({ ...prev, phoneNumber: value }))}
                    placeholder={t('teamMembers.registration.phonePlaceholder')}
                  />
                </Box>

                <SalovareSelectField
                  label={t('dataImport.customers.fields.genre')}
                  value={draft.genre}
                  onChange={(event) => setDraft((prev) => ({ ...prev, genre: event.target.value }))}
                  displayEmpty
                  renderValue={(value) => {
                    if (value === '' || value == null) return t('common.notProvided');
                    return value === '1'
                      ? t('dataImport.customers.genre.male')
                      : t('dataImport.customers.genre.female');
                  }}
                >
                  <MenuItem value="">{t('common.notProvided')}</MenuItem>
                  <MenuItem value="1">{t('dataImport.customers.genre.male')}</MenuItem>
                  <MenuItem value="2">{t('dataImport.customers.genre.female')}</MenuItem>
                </SalovareSelectField>

                <Box>
                  <Typography variant="subtitle2" sx={fieldLabelSx}>
                    {t('dataImport.customers.fields.birthDate')}
                  </Typography>
                  {selectedRow.original?.birthDate ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('dataImport.customers.fixDialog.originalValue', { value: selectedRow.original.birthDate })}
                    </Typography>
                  ) : null}
                  <SalovareDatePicker
                    value={draft.birthDate}
                    onChange={(newValue) => setDraft((prev) => ({ ...prev, birthDate: newValue }))}
                    disableFuture
                    maxDate={new Date()}
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </ScrollableDialog>
  );
};

export default FixFailedImportRowsDialog;
